'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import LLAlert from '@next-app/components/elements/LLAlert/LLAlert';
import { LoginStatus } from '@next-app/constants/constants';
import {
  isNotNullOrUndefinedorEmpty,
  totalPointsFormatted,
} from '@next-app/utils/common-utils';
import {
  selectIsSessionInfoAvailable,
  selectLoginStatus,
} from '@next-app/lib/features/InitSelectors';
import { CommerceItemInfo } from '@next-app/lib/features/checkout/Checkout.interface';
import { LineItemInterface } from '@next-app/lib/features/ShoppingCart/ShoppingCart.interface';
import dynamic from 'next/dynamic';

const CreateAccountModal = dynamic(
  () => import('@next-app/components/CreateAccountModal/CreateAccountModal'),
  { ssr: false },
);

const RewardsMessage = ({
  cartItems,
  isRewardsMember = false,
  totalPoints,
  isCheckout = false,
}: {
  cartItems: CommerceItemInfo[] | LineItemInterface[];
  isRewardsMember: boolean;
  totalPoints: number;
  isCheckout?: boolean;
}) => {
  // selectors
  const isSessionInfoAvailable = useSelector(selectIsSessionInfoAvailable);
  const loginStatus = useSelector(selectLoginStatus);

  // local state
  const [rewardMsg, setRewardMsg] = useState<React.ReactNode>('');
  const [isEligibleForRewardsMsg, setIsEligibleForRewardsMsg] = useState(false);

  //modal state
  const [showCreateAccModal, setShowCreateAccModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (!cartItems || cartItems?.length === 0) {
      return;
    }

    const cartHasNonFurnitureItems = cartItems.some((item) => !item.furniture);

    setIsEligibleForRewardsMsg(
      cartHasNonFurnitureItems &&
        isNotNullOrUndefinedorEmpty(totalPoints) &&
        totalPoints > 0,
    );
  }, [cartItems, totalPoints]);

  // decide what msg needs to be displayed
  useEffect(() => {
    if (!isSessionInfoAvailable) {
      return;
    }
    switch (loginStatus) {
      case LoginStatus.GUEST: {
        isCheckout
          ? setRewardMsg(
              <div>
                You’ll earn {totalPointsFormatted(totalPoints)} points today
                when you sign up after placing your order.
              </div>,
            )
          : setRewardMsg(
              <div>
                Your rewards are waiting!{' '}
                <Link
                  href="#"
                  data-bs-toggle="modal"
                  data-bs-target="#dynamicModalWrapper"
                  onClick={() => {
                    setShowCreateAccModal(true);
                  }}
                  className="text-decoration-underline-offset"
                >
                  Sign up
                </Link>{' '}
                for FREE to collect {totalPointsFormatted(totalPoints)} points
                and enjoy exclusive perks.
              </div>,
            );

        break;
      }

      case LoginStatus.RECOGNIZED: {
        isCheckout
          ? setRewardMsg(
              <div>
                Your {totalPointsFormatted(totalPoints)} points are waiting!
                Sign in to add them to your account and start earning rewards.
              </div>,
            )
          : setRewardMsg(
              <div>
                Your {totalPointsFormatted(totalPoints)} points are waiting!{' '}
                <Link
                  href="#"
                  data-bs-toggle="modal"
                  data-bs-target="#dynamicModalWrapper"
                  onClick={() => {
                    setShowCreateAccModal(true);
                  }}
                  className="text-decoration-underline-offset"
                >
                  Sign up
                </Link>{' '}
                to add them to your account and start earning rewards.
              </div>,
            );
        break;
      }

      case LoginStatus.LOGGED_IN: {
        if (isRewardsMember) {
          setRewardMsg(
            `You are racking up ${totalPointsFormatted(totalPoints)} points on this order! Keep earning and enjoy exclusive benefits!`,
          );
        }
        break;
      }
    }
  }, [isSessionInfoAvailable, loginStatus, isRewardsMember, totalPoints]);

  // Renderers
  const RenderCartBagImage = () => {
    return (
      <div className="pe-ll-10">
        <Image
          src="/react/assets/icons/cart-bags-icon.svg"
          alt={''}
          width={36}
          height={24}
          unoptimized={true}
        />
      </div>
    );
  };
  return (
    <>
      <Conditional
        if={isEligibleForRewardsMsg && isNotNullOrUndefinedorEmpty(rewardMsg)}
      >
        <div className="ll-fs-13-lh-18 d-print-none">
          <LLAlert type="info" icon={<RenderCartBagImage />}>
            {rewardMsg}
          </LLAlert>
        </div>
      </Conditional>
      <Conditional if={showCreateAccModal}>
        <CreateAccountModal
          setShowCreateAccModal={setShowCreateAccModal}
          setShowSuccessModal={setShowSuccessModal}
          showSuccessModal={showSuccessModal}
        />
      </Conditional>
    </>
  );
};

export default RewardsMessage;
